import { FC, ReactNode, ElementType } from 'react';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/tooltip';
import { CircleHelp } from 'lucide-react';

interface HelpTooltipProps {
  label: string;
  children: ReactNode;
  side?: 'top' | 'bottom' | 'left' | 'right';
  align?: 'start' | 'center' | 'end';
  sideOffset?: number;
  isPositionAbsolute?: boolean;
  hideArrow?: boolean;
  trigger?: ReactNode;
  heading?: ReactNode;
  headingTag?: 'h1' | 'h2' | 'h3' | 'h4' | 'p' | 'span';
  headingClassName?: string;
  disabled?: boolean;
  exposeInline?: boolean;
  className?: string;
}

export const HelpTooltip: FC<HelpTooltipProps> = ({
  label,
  children,
  side = 'top',
  align = 'center',
  sideOffset = 8,
  hideArrow = true,
  isPositionAbsolute = false,
  exposeInline = false,
  heading,
  headingTag = 'h3',
  headingClassName,
  className,
  trigger,
}) => {
  if (heading) {
    const HeadingTag = headingTag as ElementType;

    if (exposeInline) {
      return (
        <div className='w-full'>
          <HeadingTag className={headingClassName}>{heading}</HeadingTag>
          <div
            className={`text-sm text-muted-foreground mt-1 ${className || ''}`}
          >
            {children}
          </div>
        </div>
      );
    }

    return (
      <div className='flex items-center gap-2'>
        <HeadingTag className={headingClassName}>{heading}</HeadingTag>
        <Tooltip>
          <TooltipTrigger
            asChild
            className='flex items-center justify-center text-muted-foreground'
          >
            <button
              type='button'
              aria-label={label}
              className={
                isPositionAbsolute
                  ? 'absolute right-2 top-1/2 -translate-y-1/2'
                  : ''
              }
            >
              <CircleHelp className='h-4 w-4' />
            </button>
          </TooltipTrigger>
          <TooltipContent
            side={side}
            align={align}
            sideOffset={sideOffset}
            hideArrow={hideArrow}
            className={`max-w-[260px] text-wrap ${className || ''}`}
          >
            {children}
          </TooltipContent>
        </Tooltip>
      </div>
    );
  }

  // used to expose tooltips for when there is space to display them
  if (exposeInline) {
    return (
      <div className={`text-sm text-muted-foreground mt-1 ${className || ''}`}>
        {children}
      </div>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {trigger ? (
          <div aria-label={label}>{trigger}</div>
        ) : (
          <button
            type='button'
            aria-label={label}
            className={
              isPositionAbsolute
                ? 'absolute right-2 top-1/2 -translate-y-1/2'
                : 'flex items-center justify-center text-muted-foreground'
            }
          >
            <CircleHelp className='h-4 w-4' />
          </button>
        )}
      </TooltipTrigger>
      <TooltipContent
        side={side}
        align={align}
        sideOffset={sideOffset}
        hideArrow={hideArrow}
        className={`max-w-[260px] text-wrap ${className || ''}`}
      >
        {children}
      </TooltipContent>
    </Tooltip>
  );
};

export default HelpTooltip;
